import { Box } from "rebass/styled-components";

import { useNavClickTracker } from "MVVM/Hooks/useNavClickTracker";
import { useFeaturedCompetition } from "../CompetitionFeature";

import {
  INavCategory,
  INavItem,
  isPathActive,
  resolveCta,
  visibleItems,
} from "../links";
import {
  ItemAnchor,
  ItemBadge,
  ItemDescription,
  ItemGrid,
  ItemIcon,
  ItemLink,
  ItemTitle,
  ItemTitleRow,
  Panel,
  PanelBlurb,
  PanelCta,
  PanelCtaAnchor,
  PanelCtaButton,
  PanelEyebrow,
  PanelHeadline,
  PanelInner,
  PanelPitch,
} from "../styledComponents";

// Exported for the mobile sheet, which renders the same row inside an
// accordion rather than repeating the icon/badge/active markup.
export const NavItem = ({
  item,
  pathname,
  onNavigate,
  surface = "site_nav",
}: {
  item: INavItem;
  pathname: string;
  onNavigate: () => void;
  surface?: string;
}) => {
  const trackNavClick = useNavClickTracker(surface);
  const active = isPathActive(item.to, pathname);
  const handleClick = () => {
    trackNavClick(item.title, item.href || item.to);
    onNavigate();
  };
  const Icon = item.icon;
  const body = (
    <>
      <ItemTitleRow>
        <ItemIcon aria-hidden="true">
          <Icon />
        </ItemIcon>
        <ItemTitle>{item.title}</ItemTitle>
        {item.badge && <ItemBadge>{item.badge}</ItemBadge>}
      </ItemTitleRow>
      <ItemDescription>{item.description}</ItemDescription>
    </>
  );

  if (item.href)
    return (
      <ItemAnchor
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
      >
        {body}
      </ItemAnchor>
    );

  return (
    <ItemLink
      to={item.to || "/"}
      onClick={handleClick}
      aria-current={active ? "page" : undefined}
    >
      {body}
    </ItemLink>
  );
};

export const MegaMenu = ({
  category,
  isLoggedIn,
  panelId,
  pathname,
  standalone,
  onNavigate,
  onOpenSteambot,
  steambotEnrolled,
  onMouseEnter,
  onMouseLeave,
}: {
  category: INavCategory;
  isLoggedIn: boolean;
  panelId: string;
  pathname: string;
  standalone?: boolean;
  onNavigate: () => void;
  onOpenSteambot: () => void;
  steambotEnrolled: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => {
  // A live competition takes over the panel's art and its CTA, so Compete
  // points at the thing running right now rather than a static list.
  const competition = useFeaturedCompetition(
    category.featured === "competition",
  );
  // The label in the bar and this button share one resolution, so they cannot
  // send the same click to two different places, or name a place it is not
  // going.
  const { label, destination } = resolveCta(category, {
    isLoggedIn,
    steambotEnrolled,
  });
  const ctaLabel = competition ? `Enter ${competition.title}` : label;
  const ctaTo = competition
    ? `/competitions/${competition._id}`
    : (destination?.kind === "to" && destination.to) || "/";
  const ctaHref = competition
    ? undefined
    : destination?.kind === "href"
    ? destination.href
    : undefined;
  const ctaOpensModal = !competition && destination?.kind === "modal";

  return (
    <Panel
      id={panelId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={category.label}
    >
      <PanelInner $standalone={standalone}>
        {/* Left third: the panel sells while it navigates. */}
        <PanelPitch $art={category.art} $glass={category.glass}>
          <PanelEyebrow>{category.eyebrow}</PanelEyebrow>
          <PanelHeadline>{category.label}</PanelHeadline>
          <PanelBlurb>{category.blurb}</PanelBlurb>
          {ctaOpensModal ? (
            <PanelCtaButton
              $accent={category.accent}
              onClick={() => {
                onNavigate();
                onOpenSteambot();
              }}
            >
              {ctaLabel}
            </PanelCtaButton>
          ) : ctaHref ? (
            <PanelCtaAnchor
              $accent={category.accent}
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onNavigate}
            >
              {ctaLabel}
            </PanelCtaAnchor>
          ) : (
            <PanelCta to={ctaTo} $accent={category.accent} onClick={onNavigate}>
              {ctaLabel}
            </PanelCta>
          )}
        </PanelPitch>
        <Box>
          <ItemGrid>
            {visibleItems(category.items, isLoggedIn).map((item) => (
              <NavItem
                key={item.title}
                item={item}
                pathname={pathname}
                onNavigate={onNavigate}
              />
            ))}
          </ItemGrid>
        </Box>
      </PanelInner>
    </Panel>
  );
};
